import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, type NavigateOptions, type To } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@immoaktas/ui-web';

/**
 * Leave guard for pages with unsaved edits. The app uses <BrowserRouter> (no data router), so react-router's
 * useBlocker is not available. What it covers instead:
 * - reload, tab close, typed URL: the browser's own `beforeunload` prompt,
 * - in-app links (<Link>, <a href>): clicks are intercepted in the capture phase and ask in an in-app dialog,
 * - own buttons that leave (Abbrechen, Zurück): wrap their action in `guard(fn)`,
 * - programmatic navigation elsewhere (property switcher, table row clicks): `useGuardedNavigate()` asks the guard
 *   of whichever page is mounted and dirty.
 * Not covered: the browser back/forward buttons (popstate cannot be cancelled without a data router).
 * Render `prompt` somewhere in the page.
 */
type Guard = { dirty: () => boolean; ask: (leave: () => void) => void };
/** Mounted guards, newest last (a dialog's guard over its page's). */
const guards: Guard[] = [];

/** Runs `leave` right away, or after the person agreed to drop the unsaved edits of the dirty page. */
export function guardedLeave(leave: () => void) {
  const g = [...guards].reverse().find((x) => x.dirty());
  if (g) g.ask(leave);
  else leave();
}

/** `useNavigate()` that asks first when a page with unsaved edits is open (for navigation without a link). */
export function useGuardedNavigate() {
  const navigate = useNavigate();
  return useCallback((to: To | number, opts?: NavigateOptions) => guardedLeave(() => (typeof to === 'number' ? navigate(to) : navigate(to, opts))), [navigate]);
}

export function useUnsavedGuard(dirty: boolean): { prompt: ReactNode; guard: (leave: () => void) => void } {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const live = useRef(dirty);
  live.current = dirty;
  // registered while mounted: guardedLeave() from anywhere asks this page before leaving
  useEffect(() => {
    const entry: Guard = { dirty: () => live.current, ask: (leave) => setPending(() => leave) };
    guards.push(entry);
    return () => { guards.splice(guards.indexOf(entry), 1); };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (!live.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      const to = url.pathname + url.search + url.hash;
      setPending(() => () => navigate(to));
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, navigate]);

  const guard = useCallback((leave: () => void) => (live.current ? setPending(() => leave) : leave()), []);
  const leave = () => {
    const go = pending;
    setPending(null);
    live.current = false;
    go?.();
  };
  const prompt = (
    <Dialog
      open={!!pending}
      onClose={() => setPending(null)}
      title={t('ui.discard.leaveTitle')}
      actions={<><Button variant="secondary" onClick={() => setPending(null)}>{t('ui.discard.keep')}</Button><Button variant="danger" onClick={leave}>{t('ui.discard.leave')}</Button></>}
    >
      <p>{t('ui.discard.leaveText')}</p>
    </Dialog>
  );
  return { prompt, guard };
}
